import { rawDocs, docHistory } from "./docs.generated"
import type { DocCommit } from "./docs.generated"
import { formatSnakeCaseToTitle } from "~/utils/formatting"

export type { DocCommit }

export const DOCS_PATH = "/internal/docs"
export const MIN_SEARCH_QUERY_LEN = 2

type DocMeta = {
  title?: string
  description?: string
  category?: string
  tags?: string[]
  related?: string[]
  source?: string
}

export type DocFormat = "markdown" | "artifact"

export type Doc = {
  slug: string
  filename: string
  format: DocFormat
  sourceUrl?: string
  title: string
  description?: string
  lastUpdated?: string
  category: string
  tags: string[]
  related: string[]
  content: string
  history: DocCommit[]
}

const UNCATEGORIZED = "Other"

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/
const HTML_FRONTMATTER_RE = /^<!--\r?\n---\r?\n([\s\S]*?)\r?\n---\r?\n-->\r?\n([\s\S]*)$/

const isArtifactFile = (filename: string): boolean => /\.html$/i.test(filename)

const parseFrontmatter = (raw: string, re: RegExp): { meta: DocMeta; body: string } => {
  const match = raw.match(re)
  if (!match) return { meta: {}, body: raw }

  const [, block, body] = match as unknown as [string, string, string]
  const meta: DocMeta = {}

  for (const line of block.split(/\r?\n/)) {
    const m = line.match(/^([a-zA-Z_]+):\s*(.*)$/)
    if (!m) continue
    const key = m[1] as keyof DocMeta
    const value = (m[2] ?? "").trim()
    if (!value) continue

    if (value.startsWith("[") && value.endsWith("]")) {
      const items = value
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim().replace(/^["']|["']$/g, ""))
        .filter(Boolean)
      ;(meta as Record<string, unknown>)[key] = items
    } else {
      ;(meta as Record<string, unknown>)[key] = value.replace(/^["']|["']$/g, "")
    }
  }

  return { meta, body }
}

const basename = (p: string): string => {
  const i = p.lastIndexOf("/")
  return i < 0 ? p : p.slice(i + 1)
}

const dirOf = (p: string): string | null => {
  const i = p.lastIndexOf("/")
  return i < 0 ? null : p.slice(0, i)
}

const firstSegment = (p: string): string | null => {
  const i = p.indexOf("/")
  return i < 0 ? null : p.slice(0, i)
}

const isLanding = (filename: string): boolean =>
  dirOf(filename) !== null && basename(filename).toLowerCase() === "readme.md"

const filenameToSlug = (filename: string): string => {
  const stem = filename.replace(/\.(md|html)$/i, "")
  const dir = dirOf(stem)
  const base = basename(stem)
  const path = dir && base.toLowerCase() === "readme" ? dir : stem
  return path.toLowerCase().replace(/_/g, "-").replace(/\//g, "-")
}

const stripLeadingH1 = (body: string): string => body.replace(/^\s*#\s+.+(\r?\n)+/, "")

const htmlToText = (html: string): string =>
  html
    .replace(/<(style|script|title)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()

const cleanH1 = (h1: string, filename: string): string => {
  const base = basename(filename)
  const prefixRe = new RegExp(`^${base.replace(/\./g, "\\.")}\\s*[—-]\\s*`, "i")
  const stripped = h1.replace(prefixRe, "").trim()
  const emDashIdx = stripped.indexOf(" — ")
  return (emDashIdx > 0 ? stripped.slice(0, emDashIdx) : stripped).trim()
}

const deriveCategory = (filename: string, meta: DocMeta): string => {
  if (meta.category) return meta.category
  const seg = firstSegment(filename)
  if (seg) return formatSnakeCaseToTitle(seg)
  return UNCATEGORIZED
}

type IndexedDoc = Doc & { _text: string; _textLower: string }

const buildDoc = (filename: string, raw: string): IndexedDoc => {
  const slug = filenameToSlug(filename)
  const history = docHistory[filename] ?? []
  const common = { slug, filename, lastUpdated: history[0]?.date, history }

  if (isArtifactFile(filename)) {
    const { meta, body } = parseFrontmatter(raw, HTML_FRONTMATTER_RE)
    const text = htmlToText(body)
    return {
      ...common,
      format: "artifact",
      sourceUrl: meta.source,
      title:
        meta.title ??
        body.match(/<title>([^<]+)<\/title>/i)?.[1]?.trim() ??
        formatSnakeCaseToTitle(basename(filename).replace(/\.html$/i, "")),
      description: meta.description,
      category: deriveCategory(filename, meta),
      tags: meta.tags ?? [],
      related: meta.related ?? [],
      content: body,
      _text: text,
      _textLower: text.toLowerCase(),
    }
  }

  const { meta, body } = parseFrontmatter(raw, FRONTMATTER_RE)
  const h1 = body.match(/^#\s+(.+)$/m)?.[1]?.trim()
  const content = stripLeadingH1(body)
  return {
    ...common,
    format: "markdown",
    title:
      meta.title ??
      (h1
        ? cleanH1(h1, filename)
        : formatSnakeCaseToTitle(basename(filename).replace(/\.md$/i, ""))),
    description: meta.description,
    category: deriveCategory(filename, meta),
    tags: meta.tags ?? [],
    related: meta.related ?? [],
    content,
    _text: content,
    _textLower: content.toLowerCase(),
  }
}

const buildDocs = (): IndexedDoc[] => {
  const docs = Object.entries(rawDocs).map(([filename, raw]) => buildDoc(filename, raw))
  return docs.sort((a, b) => a.title.localeCompare(b.title))
}

const buildDocsByCategory = (): { category: string; docs: Doc[] }[] => {
  const groups = new Map<string, Doc[]>()
  for (const doc of docs) {
    if (isLanding(doc.filename)) continue
    const list = groups.get(doc.category) ?? []
    list.push(doc)
    groups.set(doc.category, list)
  }
  return [...groups.entries()]
    .map(([category, docs]) => ({ category, docs }))
    .sort((a, b) => {
      if (a.category === UNCATEGORIZED) return 1
      if (b.category === UNCATEGORIZED) return -1
      return a.category.localeCompare(b.category)
    })
}

const buildDocsByFilename = (docs: readonly Doc[]): Map<string, Doc> => {
  const map = new Map<string, Doc>()
  for (const doc of docs) {
    map.set(doc.filename.toLowerCase(), doc)
  }
  for (const doc of docs) {
    const base = basename(doc.filename).toLowerCase()
    if (!map.has(base)) map.set(base, doc)
  }
  return map
}

const buildCategoryLandings = (docs: readonly Doc[]): Map<string, Doc> => {
  const map = new Map<string, Doc>()
  for (const doc of docs) {
    if (isLanding(doc.filename) && !map.has(doc.category)) {
      map.set(doc.category, doc)
    }
  }
  return map
}

const allDocs = buildDocs()
export const docs: readonly Doc[] = allDocs
export const docsByCategory = buildDocsByCategory()
export const docsBySlug = new Map(allDocs.map((d) => [d.slug, d]))
export const docsByFilename = buildDocsByFilename(allDocs)
export const categoryLandings = buildCategoryLandings(allDocs)

export const resolveDocLink = (href: string): string | null => {
  const m = href.match(/^(?:\.\/)?([\w\-/]+)\.md(#.*)?$/)
  if (!m) return null
  const doc = docsByFilename.get(`${m[1]?.toLowerCase()}.md`)
  if (!doc) return null
  return `${DOCS_PATH}/${doc.slug}${m[2] ?? ""}`
}

export const headingId = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")

export type DocSearchHit = {
  doc: Doc
  matchIn: "title" | "description" | "tags" | "body"
  snippet: string
}

const SNIPPET_RADIUS = 60

const buildSnippet = (text: string, idx: number, qLen: number): string => {
  const start = Math.max(0, idx - SNIPPET_RADIUS)
  const end = Math.min(text.length, idx + qLen + SNIPPET_RADIUS)
  const prefix = start > 0 ? "…" : ""
  const suffix = end < text.length ? "…" : ""
  return prefix + text.slice(start, end).replace(/\s+/g, " ").trim() + suffix
}

export const searchDocs = (rawQuery: string): DocSearchHit[] => {
  const q = rawQuery.trim().toLowerCase()
  if (q.length < MIN_SEARCH_QUERY_LEN) return []

  const hits: DocSearchHit[] = []
  for (const doc of allDocs) {
    if (doc.title.toLowerCase().includes(q)) {
      hits.push({ doc, matchIn: "title", snippet: doc.title })
      continue
    }
    if (doc.description?.toLowerCase().includes(q)) {
      hits.push({ doc, matchIn: "description", snippet: doc.description })
      continue
    }
    const tagHit = doc.tags.find((t) => t.toLowerCase().includes(q))
    if (tagHit) {
      hits.push({ doc, matchIn: "tags", snippet: doc.tags.join(", ") })
      continue
    }
    const bodyIdx = doc._textLower.indexOf(q)
    if (bodyIdx >= 0) {
      hits.push({
        doc,
        matchIn: "body",
        snippet: buildSnippet(doc._text, bodyIdx, q.length),
      })
    }
  }
  return hits
}
